import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  searchAddresses,
  reverseGeocode,
  getCurrentPosition,
  simplifyAddress,
  analyzeTextPrecision,
  GeocodeSuggestion,
} from '../services/geocoding';
import { useDebounce } from '../hooks/useDebounce';
import { useLanguage } from '../contexts/LanguageContext';
import './AddressMapSearch.css';

// Fix Leaflet default icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

interface AddressMapSearchProps {
  onAddressSelect: (address: GeocodeSuggestion) => void;
  initialAddress?: Partial<GeocodeSuggestion>;
  mode?: 'standard' | 'high-quality'; // Search mode: high-quality uses Photon + Nominatim
}

/**
 * Component to handle map events and update marker position
 */
function MapController({
  position,
  onPositionChange,
}: {
  position: [number, number] | null;
  onPositionChange: (lat: number, lng: number) => void;
}) {
  const map = useMap();

  // Listen to map clicks
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng.lat, e.latlng.lng);
    },
  });

  // Fly to position when it changes
  useEffect(() => {
    if (position) {
      map.flyTo(position, 16, { duration: 0.5 });
    }
  }, [position, map]);

  return null;
}

/**
 * Address Map Search Component
 * Combines autocomplete search with interactive map selection
 */
const AddressMapSearch: React.FC<AddressMapSearchProps> = ({
  onAddressSelect,
  initialAddress,
  mode = 'standard', // Default to standard mode
}) => {
  const { t } = useLanguage();

  // Search input state
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Map state
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(
    initialAddress?.latitude && initialAddress?.longitude
      ? [initialAddress.latitude, initialAddress.longitude]
      : null
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // User's geolocation for prioritizing nearby results
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(
    null
  );

  // Selected address
  const [selectedAddress, setSelectedAddress] = useState<GeocodeSuggestion | null>(null);

  // Editable address field
  const [editableAddress, setEditableAddress] = useState('');

  // Track if user manually edited the address
  const [isManuallyEdited, setIsManuallyEdited] = useState(false);

  // Track last geocoded position to avoid duplicate calls
  const lastGeocodedPosition = useRef<string | null>(null);

  // Refs
  const searchRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<L.Marker>(null);
  const addressInfoRef = useRef<HTMLDivElement>(null);

  // Debounced search
  const debouncedSearch = useDebounce(searchInput, 300);

  // Debounced marker position for reverse geocoding
  const debouncedMarkerPosition = useDebounce(markerPosition, 800);

  // Initialize with initial address
  useEffect(() => {
    if (initialAddress?.display_name) {
      setSearchInput(initialAddress.display_name);
    }
  }, [initialAddress]);

  // Get user's geolocation on mount (silently, for search prioritization)
  useEffect(() => {
    // Try to get user location for better search results
    const fetchUserLocation = async () => {
      try {
        const position = await getCurrentPosition();
        setUserLocation({
          latitude: position.latitude,
          longitude: position.longitude,
        });
        console.log('📍 User location acquired for search prioritization');
      } catch (error) {
        // Silently fail - user location is optional for search
        console.log('ℹ️ User location not available, search will not be geographically biased');
      }
    };

    fetchUserLocation();
  }, []);

  // Autocomplete search with geographic prioritization
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (debouncedSearch.length >= 3) {
        setIsLoading(true);
        // Pass user location to prioritize nearby results
        const results = await searchAddresses(
          debouncedSearch,
          5,
          userLocation?.latitude,
          userLocation?.longitude,
          mode // Use mode prop (high-quality for registration, standard for map)
        );
        setSuggestions(results);
        setShowSuggestions(true);
        setIsLoading(false);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();
  }, [debouncedSearch, userLocation, mode]);

  // Update parent whenever marker position, selected address, or editable address changes
  useEffect(() => {
    if (markerPosition && selectedAddress) {
      // Use editableAddress for display_name (user's customized version)
      // Keep original street_address (just the street part, not full address)
      // Use current marker coordinates, not the original suggestion coordinates
      const addressWithMarkerCoords: GeocodeSuggestion = {
        ...selectedAddress,
        display_name: editableAddress || selectedAddress.display_name,
        // Don't overwrite street_address with editableAddress - keep the original street part
        latitude: markerPosition[0],
        longitude: markerPosition[1],
      };
      console.log('🗺️ AddressMapSearch: Sending address to parent:', {
        display_name: addressWithMarkerCoords.display_name,
        latitude: addressWithMarkerCoords.latitude,
        longitude: addressWithMarkerCoords.longitude,
        city: addressWithMarkerCoords.city,
        country: addressWithMarkerCoords.country,
      });
      onAddressSelect(addressWithMarkerCoords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerPosition, selectedAddress, editableAddress]);

  // Reverse geocode marker position
  useEffect(() => {
    const reverseGeocodePosition = async () => {
      if (!debouncedMarkerPosition || isDragging || isManuallyEdited) {
        return;
      }

      const [lat, lng] = debouncedMarkerPosition;
      const positionKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;

      // Skip if we already geocoded this exact position
      if (lastGeocodedPosition.current === positionKey) {
        return;
      }

      setIsLoading(true);
      const address = await reverseGeocode(lat, lng);

      if (address) {
        lastGeocodedPosition.current = positionKey;
        setSelectedAddress(address);
        setEditableAddress(simplifyAddress(address));
        // onAddressSelect will be called by the effect above
      }
      setIsLoading(false);
    };

    reverseGeocodePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedMarkerPosition, isDragging, isManuallyEdited]);

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: GeocodeSuggestion) => {
    setSearchInput(suggestion.display_name);
    setMarkerPosition([suggestion.latitude, suggestion.longitude]);
    setSelectedAddress(suggestion);
    setEditableAddress(simplifyAddress(suggestion));
    setShowSuggestions(false);
    setIsManuallyEdited(false); // Reset manual edit flag
    lastGeocodedPosition.current = `${suggestion.latitude.toFixed(6)},${suggestion.longitude.toFixed(6)}`;
    // onAddressSelect will be called by the effect above
  };

  // Handle editable address change
  const handleEditableAddressChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newAddress = e.target.value;
    setEditableAddress(newAddress);
    setIsManuallyEdited(true); // Mark as manually edited
    // Note: We don't update selectedAddress here to keep the original detected address visible
    // The editableAddress will be used when sending to parent via onAddressSelect
  };

  // Handle map click
  const handleMapClick = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    setIsManuallyEdited(false); // Allow reverse geocoding for new position
  };

  // Handle marker drag start
  const handleMarkerDragStart = () => {
    setIsDragging(true);
    setShowSuggestions(false); // Close suggestions dropdown while dragging
  };

  // Handle marker drag
  const handleMarkerDrag = (e: L.DragEndEvent) => {
    const { lat, lng } = e.target.getLatLng();
    setMarkerPosition([lat, lng]);
    setIsDragging(false);
    setIsManuallyEdited(false); // Allow reverse geocoding for new position
  };

  // Handle geolocation button
  const handleGeolocation = async () => {
    try {
      setIsLoading(true);
      const { latitude, longitude } = await getCurrentPosition();
      setMarkerPosition([latitude, longitude]);
      setIsManuallyEdited(false); // Allow reverse geocoding for new position
    } catch (error) {
      console.error('Geolocation error:', error);
      alert('Impossible d\'accéder à votre localisation. Vérifiez les autorisations.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle direct address validation (when user types full address)
  const handleDirectAddressValidation = async (address: string) => {
    if (address.length < 10) return; // Too short to be a complete address

    setIsLoading(true);
    try {
      // Try to search for this exact address with user location bias
      const results = await searchAddresses(
        address,
        1,
        userLocation?.latitude,
        userLocation?.longitude,
        mode // Use mode prop
      );
      if (results.length > 0) {
        handleSuggestionClick(results[0]);
      } else {
        // If no geocoding result, show error - don't accept invalid address
        alert('Impossible de localiser cette adresse. Veuillez sélectionner une suggestion ou placer le marqueur sur la carte.');
        setSearchInput('');
      }
    } catch (error) {
      console.error('Direct validation error:', error);
      alert('Erreur lors de la recherche de l\'adresse. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        if (showSuggestions && suggestions.length > 0) {
          e.preventDefault();
          setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (showSuggestions && suggestions.length > 0) {
          // If a suggestion is selected via arrows, use it; otherwise use the first result
          if (selectedSuggestionIndex >= 0) {
            handleSuggestionClick(suggestions[selectedSuggestionIndex]);
          } else {
            handleSuggestionClick(suggestions[0]);
          }
        } else if (searchInput.trim().length > 0) {
          // No suggestions: validate directly what the user typed
          handleDirectAddressValidation(searchInput.trim());
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to address info section when it appears
  useEffect(() => {
    if (selectedAddress && addressInfoRef.current) {
      setTimeout(() => {
        addressInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [selectedAddress]);

  // Generate precision dots with color
  const renderPrecisionDots = (score: number, color: string) => {
    const dots = [];
    for (let i = 0; i < 5; i++) {
      dots.push(
        <span
          key={i}
          style={{
            color: i < score ? color : '#ddd',
            marginRight: '2px',
            fontSize: '14px'
          }}
        >
          ●
        </span>
      );
    }
    return dots;
  };

  return (
    <div className="address-map-search">
      {/* Search Bar */}
      <div className="search-container" ref={searchRef}>
        <div className="search-input-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder={t('auth.register.addressSearch.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onInput={(e) => {
              // Détecte aussi les changements via autocomplete du navigateur
              const newValue = (e.target as HTMLInputElement).value;
              if (newValue !== searchInput) {
                setSearchInput(newValue);
              }
            }}
            autoComplete="street-address"
            name="street-address"
          />
          {isLoading && !isDragging ? (
            <span className="loading-spinner">⏳</span>
          ) : (
            <button
              type="button"
              className="search-button"
              onClick={() => {
                if (searchInput.trim() && suggestions.length > 0) {
                  handleSuggestionClick(suggestions[0]);
                }
              }}
              disabled={!searchInput.trim() || suggestions.length === 0}
              title="Rechercher"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="search-icon-svg">
                <circle cx="10" cy="10" r="7"/>
                <line x1="15" y1="15" x2="22" y2="22"/>
              </svg>
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="suggestions-dropdown">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`suggestion-item ${
                  index === selectedSuggestionIndex ? 'selected' : ''
                }`}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <div className="suggestion-main">{suggestion.display_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="map-wrapper">
        <MapContainer
          center={markerPosition || [48.8566, 2.3522]} // Default: Paris
          zoom={markerPosition ? 16 : 12}
          style={{ height: '100%', width: '100%' }}
          className="leaflet-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {markerPosition && (
            <Marker
              position={markerPosition}
              draggable={true}
              ref={markerRef}
              eventHandlers={{
                dragstart: handleMarkerDragStart,
                dragend: handleMarkerDrag,
              }}
            />
          )}

          <MapController position={markerPosition} onPositionChange={handleMapClick} />
        </MapContainer>

        {/* Geolocation Button */}
        <button type="button" className="btn-geolocation" onClick={handleGeolocation} title={t('auth.register.addressSearch.locateMe')}>
          {t('auth.register.addressSearch.locateMe')}
        </button>
      </div>

      {/* Address Info */}
      {selectedAddress && (
        <div className="address-info" ref={addressInfoRef}>
          {/* Editable Address Field */}
          <div className="editable-address-section">
            <strong className="suggestion-title">{t('auth.register.addressSearch.suggestionTitle')}</strong>

            {/* Instruction AVANT le textarea */}
            <label htmlFor="editable-address" className="editable-label" style={{ marginBottom: '8px' }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="modifier-icon" style={{ width: '16px', height: '16px' }}>
                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              {t('auth.register.addressSearch.editHintLine1')}
            </label>

            <textarea
              id="editable-address"
              className="editable-address-input"
              value={editableAddress}
              onChange={handleEditableAddressChange}
              placeholder={t('auth.register.addressSearch.detailsPlaceholder')}
              rows={2}
            />

            {/* Instruction APRÈS le textarea */}
            <p className="editable-hint" style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
              {t('auth.register.addressSearch.editHintLine2')}
            </p>
          </div>

          {(() => {
            const precision = analyzeTextPrecision(editableAddress);
            const getPrecisionMessage = (score: number) => {
              switch (score) {
                case 5: return t('auth.register.addressSearch.precisionMessages.complete');
                case 4: return t('auth.register.addressSearch.precisionMessages.addNumber');
                case 3: return t('auth.register.addressSearch.precisionMessages.addStreet');
                case 2: return t('auth.register.addressSearch.precisionMessages.addDetails');
                case 1: return t('auth.register.addressSearch.precisionMessages.tooShort');
                default: return t('auth.register.addressSearch.precisionMessages.none');
              }
            };
            return (
              <div className="address-precision" style={{ color: precision.color }}>
                <strong>{t('auth.register.addressSearch.precision')}</strong>{' '}
                {renderPrecisionDots(precision.score, precision.color)}{' '}
                <span style={{ marginLeft: '8px' }}>{getPrecisionMessage(precision.score)}</span>
              </div>
            );
          })()}
        </div>
      )}

    </div>
  );
};

export default AddressMapSearch;
